
import { Component, Ref, mixins, namespace, Watch } from 'nuxt-property-decorator';
import {
  FeatureFlag,
  BillingInfoDto,
  PackageTier,
  UpgradePricingResponseDto,
  PaymentMethod,
  isSuccess,
  isValidationError,
  tryGetClientSecretCode,
  UpgradeOrganizationResponse,
  TypeOrError,
  PackageOption,
  preparePackageOption,
  AddOnOption,
  prepareAddOnOption,
  PlanDto,
  Role,
  UpgradePricingModel,
  OrganizationCurrency,
} from 'fourwaves-shared';
import { BillingInfoFieldset, InputText, Alert } from 'fourwaves-shared/components';
import FullscreenModal from '../layout/FullscreenModal.vue';
import UpgradeCheckoutErrorModal from './UpgradeCheckoutErrorModal.vue';
import {
  featureFlagAddOnTypeMap,
  getFeatureFlagByAddOnType,
  getFeatureFlagByPackageTier,
} from '~/services/featureMapping';
import CheckoutAddOnCheckboxGroup from '~/components/checkout/CheckoutAddOnCheckboxGroup.vue';
import CheckoutPackageSelect from '~/components/checkout/CheckoutPackageSelect.vue';
import CheckoutPaymentMethods from '~/components/checkout/CheckoutPaymentMethods.vue';
import CheckoutMaxEventSelect from '~/components/checkout/CheckoutMaxEventSelect.vue';
import CheckoutTermLength from '~/components/checkout/CheckoutTermLength.vue';
import UpgradePricingTile from '~/components/upgrade/UpgradePricingTile.vue';
import { OrganizationMixin } from '~/mixins';
import { UpgradeCheckoutEvents, UpgradeCheckoutState } from './types';
import CheckoutPlanTile from '~/components/checkout/CheckoutPlanTile.vue';
import { TranslateResult } from 'vue-i18n';

const SettingsModule = namespace('settings');
const EventsModule = namespace('events');
const OrganizationsModule = namespace('organizations');

interface PlanModel {
  package: string;
  addOns: string[];
  maxActiveEvents: number | null;
  termLengthInYears: number | null;
}

@Component({
  components: {
    FullscreenModal,
    CheckoutAddOnCheckboxGroup,
    CheckoutPackageSelect,
    CheckoutPaymentMethods,
    BillingInfoFieldset,
    InputText,
    UpgradeCheckoutErrorModal,
    CheckoutMaxEventSelect,
    CheckoutTermLength,
    Alert,
    UpgradePricingTile,
    CheckoutPlanTile,
  },
  $_veeValidate: {
    validator: 'new',
  },
})
export default class UpgradeCheckout extends mixins(OrganizationMixin) {
  @SettingsModule.Getter readonly isSuperAdmin!: boolean;
  @SettingsModule.Getter hasOrganizationRole!: (organizationId: string, role: Role) => boolean;
  @EventsModule.Action refreshContext!: () => Promise<boolean>;
  @OrganizationsModule.Action fetchOrganizations!: () => Promise<void>;

  @Ref() readonly checkoutModal?: FullscreenModal;
  @Ref() readonly errorModal?: UpgradeCheckoutErrorModal;
  @Ref() readonly checkoutPaymentMethods?: CheckoutPaymentMethods;

  readonly UpgradeCheckoutState = UpgradeCheckoutState;

  state = UpgradeCheckoutState.Default;
  selectedFeature: FeatureFlag | null = null;
  isOpen = false;
  currentStep = 0;
  plans: PlanDto[] = [];
  addOns: Record<string, AddOnOption> | null = null;
  packages: Record<string, PackageOption> | null = null;
  pricing: UpgradePricingResponseDto | null = null;
  currentPlanPricing: UpgradePricingResponseDto | null = null;
  paymentMethod: PaymentMethod | null = null;
  model: PlanModel = {
    package: '',
    addOns: [],
    maxActiveEvents: null,
    termLengthInYears: null,
  };

  billingInfo: BillingInfoDto = {
    name: '',
    civicAddress: {
      addressLine1: '',
      city: '',
      countryRegion: '',
      stateProvince: '',
      postalCode: '',
    },
  };
  useOrgInfo = true;
  purchaseOrderNumber = '';

  get isCustomMode(): boolean {
    return this.isSuperAdmin;
  }

  get isAdministrator(): boolean {
    if (!this.organization) return false;
    return this.hasOrganizationRole(this.organization.id, Role.Administrator);
  }

  get currentPlan(): PlanDto | null {
    if (!this.organization) return null;
    return this.plans.find(({ id }) => id === this.organization?.currentPlanId) || null;
  }

  get programmedPlan(): PlanDto | null {
    if (!this.currentPlan) return null;
    return (
      this.plans.find(({ startDate }) =>
        this.$moment(startDate).isSameOrAfter(this.$moment(this.currentPlan?.endDate)),
      ) || null
    );
  }

  get isSingleStep(): boolean {
    return this.state === UpgradeCheckoutState.AddOns || this.state === UpgradeCheckoutState.AddEvents;
  }

  get steps(): TranslateResult[] {
    return this.isSingleStep
      ? []
      : [
          this.$t('checkout.packages.progress_title'),
          this.$t('checkout.add_ons.progress_title'),
          this.$t('checkout.payment.progress_title'),
        ];
  }

  get isRenewing(): boolean {
    return this.state === UpgradeCheckoutState.Renew;
  }

  get actionButtonLabel(): TranslateResult {
    if (this.isRenewing) return this.$t('checkout.confirm');
    if (this.pricing && this.pricing.total === 0) return this.$t('checkout.update_plan');
    return this.$t('checkout.pay_now');
  }

  get preSelectedAddOn(): AddOnOption | null {
    if (!this.isSingleStep || !this.selectedFeature || !this.addOns) return null;
    const addOnType = featureFlagAddOnTypeMap[this.selectedFeature];
    return Object.values(this.addOns).find(addon => addon.type === addOnType) || null;
  }

  get showPaymentStep(): boolean {
    return this.isSingleStep || this.currentStep === 2;
  }

  get isWaiting(): boolean {
    return this.$wait.is('pay');
  }

  get renewPlanAlert(): TranslateResult {
    if (!this.currentPlan) return '';
    if (!this.isRenewing) return '';
    const userTimezone = this.$moment.tz.guess(true);
    const endMoment = this.$moment(this.currentPlan.endDate).tz(userTimezone);
    return this.$t('checkout.renew_alert', { date: endMoment.format('LL') });
  }

  get programmedPlanAlert(): TranslateResult {
    if (!this.programmedPlan) return '';
    if (this.isRenewing) return '';
    const userTimezone = this.$moment.tz.guess(true);
    const startMoment = this.$moment(this.programmedPlan.startDate).tz(userTimezone);
    return this.$t('checkout.programmed_alert', { date: startMoment.format('LL') });
  }

  get disableInvoicing(): boolean {
    return this.isSuperAdmin ? false : !this.organization?.isVerified;
  }

  get disablePaymentMethod(): boolean {
    if (this.isWaiting) return true;
    return !this.pricing || !this.pricing.items.length || this.pricing.total === 0 ? true : false;
  }

  get isPaymentMethodRequired(): boolean {
    return this.pricing ? this.pricing.total > 0 : false;
  }

  get disableCheckout(): boolean {
    return !this.pricing || !this.pricing.items.length || this.$wait.is('getPricing') ? true : false;
  }

  get hasPayingPlan(): boolean {
    if (this.currentPlan?.package.tier !== PackageTier.Free || this.currentPlan?.addOns.length) return true;
    return false;
  }

  get providedBillingInfo(): BillingInfoDto | null {
    if (!this.organization || !this.organization.civicAddress) return null;
    return {
      name: this.transl8(this.organization.name),
      civicAddress: this.organization.civicAddress,
    };
  }

  get checkoutCurrency(): OrganizationCurrency {
    return this.pricing?.currency || this.organization?.currency || 'USD';
  }

  get pricingModel(): UpgradePricingModel {
    return {
      organizationId: this.organization?.id || '',
      package:
        this.packages && this.model.package
          ? {
              id: this.packages[this.model.package].id,
              cost: this.packages[this.model.package].cost,
              fee: this.packages[this.model.package].fee,
            }
          : null,
      addOns: this.addOns
        ? Object.values(this.addOns)
            .filter(addon => this.model.addOns.includes(addon.id))
            .map(({ id, cost }) => {
              return { id, cost };
            })
        : [],
      billingInfo: { ...this.billingInfo, email: this.$auth.user?.email || '' },
      renew: this.isRenewing,
      maxActiveEvents: this.model.maxActiveEvents || null,
      termLengthInYears: this.model.termLengthInYears || null,
    };
  }

  @Watch('model.maxActiveEvents')
  async onMaxActiveEventChange() {
    if (!this.showPaymentStep) return;
    await this.getPricing();
  }

  @Watch('model.termLengthInYears')
  async onTermLengthChange() {
    if (!this.showPaymentStep) return;
    await this.getPricing();
  }

  @Watch('checkoutCurrency')
  async onCurrencyChange(newCurrency: OrganizationCurrency, previousCurrency: OrganizationCurrency) {
    if (!this.isOpen) return;
    if (newCurrency === previousCurrency) return;
    this.addOns = await this.getAddOns();
    this.packages = await this.getPackages();
    await this.getCurrentPlanPricing();
    await this.getPricing();
  }

  @Watch('$route')
  onRouteChange() {
    if (!this.isOpen) return;
    this.closeCheckoutModal();
    this.errorModal?.close();
  }

  created() {
    this.$nuxt.$on(UpgradeCheckoutEvents.Open, this.openCheckout);
  }

  beforeDestroy() {
    this.$nuxt.$off(UpgradeCheckoutEvents.Open, this.openCheckout);
  }

  public async openCheckout(state: UpgradeCheckoutState, feature: FeatureFlag | null = null) {
    if (this.errorModal?.hasContextError) {
      this.errorModal?.open();
    } else {
      this.state = state;
      this.selectedFeature = feature;
      await this.initCheckoutProcess();
    }
  }

  public async initCheckoutProcess() {
    if (!this.organization) return;

    this.$wait.start(`initCheckout-${this.selectedFeature}`);
    this.plans = await this.$api.getPlans({ organizationId: this.organization.id });
    this.addOns = await this.getAddOns();
    this.packages = await this.getPackages();
    await this.getPricing();
    await this.getCurrentPlanPricing();
    this.prepareModel();

    this.$wait.end(`initCheckout-${this.selectedFeature}`);
    this.$nuxt.$emit(UpgradeCheckoutEvents.Initiated);
    this.checkoutModal?.open();
    this.isOpen = true;
  }

  public async getAddOns(): Promise<Record<string, AddOnOption> | null> {
    if (!this.organization) return null;
    const addOns = await this.$api.getAddOnsWithOrganizationStatus({
      organizationId: this.organization.id,
      currency: this.checkoutCurrency,
    });
    return Object.assign({}, ...addOns.map(addon => ({ [addon.id]: prepareAddOnOption(addon) })));
  }

  public async getPackages(): Promise<Record<string, PackageOption>> {
    const packages = await this.$api.getPackages({ currency: this.checkoutCurrency });

    const filteredPackages = packages.filter(
      ({ tier }) =>
        tier === PackageTier.Essentials || tier === PackageTier.Advanced || tier === PackageTier.Professional,
    );
    return Object.assign(
      {},
      ...filteredPackages.map(pck => ({
        [pck.id]: preparePackageOption({ pck, renew: this.isRenewing, eventActivePackage: this.currentPlan?.package }),
      })),
    );
  }

  public prepareModel() {
    if (!this.organization) return;
    if (!this.currentPlan) return;

    this.model.maxActiveEvents = this.currentPlan.maxActiveEvents;
    this.model.termLengthInYears = this.currentPlan.termLengthInYears;
    this.billingInfo.name = this.transl8(this.organization.name);

    if (this.organization.civicAddress) {
      this.billingInfo.civicAddress = this.organization.civicAddress;
    }

    if (!this.hasPayingPlan) {
      this.model.maxActiveEvents = this.organization.activeEventCount || 1;
      this.model.termLengthInYears = 1;
    }

    if (this.state === UpgradeCheckoutState.AddEvents) {
      this.model.maxActiveEvents = this.currentPlan.maxActiveEvents + 1;
    }

    if (this.state === UpgradeCheckoutState.AddOns && this.preSelectedAddOn) {
      this.model.addOns = [this.preSelectedAddOn.id];
    }

    if (this.isRenewing) {
      const plan = this.programmedPlan || this.currentPlan;
      if (!plan) return;
      this.selectTierFromPlan(plan);
      this.selectAddOnsFromPlan(plan);
      this.model.maxActiveEvents = plan.maxActiveEvents;
      this.model.termLengthInYears = plan.termLengthInYears;
    }
  }

  public selectTierFromPlan(plan: PlanDto) {
    if (!this.packages) return;
    const planTier = plan.package.tier;
    this.model.package = Object.values(this.packages).find(({ tier }) => tier === planTier)?.id || '';
  }

  public selectAddOnsFromPlan(plan: PlanDto) {
    this.model.addOns = plan.addOns.map(addon => {
      if (!this.addOns) return '';
      return Object.values(this.addOns).find(({ type }) => type === addon.type)?.id || '';
    });
  }

  public async getPricing() {
    this.$wait.start('getPricing');
    this.pricing = await this.$api.getUpgradePricing(this.pricingModel);
    this.$wait.end('getPricing');
  }

  public async getCurrentPlanPricing() {
    if (!this.organization) return;
    this.currentPlanPricing = (
      await this.$api.getPlanCost({
        planId: this.organization.currentPlanId,
        billingInfo: { ...this.billingInfo, email: this.$auth.user?.email || '' },
      })
    ).pricing;
  }

  public checkForDisablingAddOns() {
    if (!this.packages || !this.addOns) return;
    const selectedTier = this.model.package ? this.packages[this.model.package].tier : null;
    Object.values(this.addOns).forEach(addon => {
      if (!selectedTier) {
        addon.disabled = false;
      } else {
        const addOnFeature = getFeatureFlagByAddOnType(addon.type);
        const isAddOnPartOfSelectedTier = !!getFeatureFlagByPackageTier(selectedTier).find(
          flag => flag === addOnFeature,
        );
        addon.disabled = isAddOnPartOfSelectedTier;
      }
    });
  }

  public async pay() {
    if (!(await this.$validator.validateAll())) return;

    this.$wait.start('pay');
    let paymentMethodId = '';
    if (this.paymentMethod === PaymentMethod.Stripe) {
      paymentMethodId = (await this.checkoutPaymentMethods?.stripe?.initiatePayment()) || '';
    }
    const result = await this.$api.upgradeOrganization({
      ...this.pricingModel,
      purchaseOrderNumber: this.purchaseOrderNumber || '',
      paymentMethod: this.paymentMethod || PaymentMethod.Void,
      stripePayload: paymentMethodId ? { paymentMethodId } : null,
    });
    await this.onPaymentAttempt(result, paymentMethodId);

    this.$wait.end('pay');
  }

  public async onPaymentAttempt(result: TypeOrError<UpgradeOrganizationResponse>, paymentMethodId?: string) {
    if (isSuccess(result)) {
      await this.fetchOrganizations();
      if (!this.isInOrganizerDashboard) await this.refreshContext();
      this.$nuxt.$emit(UpgradeCheckoutEvents.Completed);
      this.closeCheckoutModal();
      if (result.upgradeTransactionId) {
        this.$toast.success(this.$t('checkout.payment.success'));
      } else {
        this.$toast.success(this.$t('checkout.payment.success_no_transactions'));
      }
    } else if (isValidationError(result)) {
      const clientSecretCode = tryGetClientSecretCode(result);
      if (clientSecretCode && paymentMethodId) {
        await this.stripeRequiresActionCallBack(paymentMethodId, clientSecretCode);
      } else {
        this.feedErrorBag(result);
      }
    } else {
      this.$toast.error(this.$t('shared.error.server_validation.payment_error'));
    }
  }

  public async stripeRequiresActionCallBack(paymentMethodId: string, clientSecretCode: string) {
    this.$toast.info(this.$t('shared.error.server_validation.payment_requires_action'));
    const paymentIntentId = await this.checkoutPaymentMethods?.stripe?.confirmCardPayment(clientSecretCode);
    if (paymentIntentId) {
      const result = await this.$api.upgradeOrganization({
        ...this.pricingModel,
        purchaseOrderNumber: this.purchaseOrderNumber,
        paymentMethod: PaymentMethod.Stripe,
        stripePayload: { paymentMethodId, paymentIntentId },
      });
      await this.onPaymentAttempt(result);
    }
  }

  public async nextStep() {
    if (!(await this.$validator.validateAll())) return;
    if (this.currentStep === 0) this.checkForDisablingAddOns();
    if (this.currentStep === 1 && this.pricing) {
      await this.getPricing();
    }

    this.currentStep++;
  }

  public resetCheckout() {
    this.state = UpgradeCheckoutState.Default;
    this.model = {
      package: '',
      addOns: [],
      maxActiveEvents: null,
      termLengthInYears: null,
    };
    this.pricing = null;
    this.paymentMethod = null;
    this.currentStep = 0;
    this.selectedFeature = null;
    this.useOrgInfo = true;
    this.purchaseOrderNumber = '';
    this.resetBillingInfo();
  }

  public resetBillingInfo() {
    this.billingInfo = {
      name: '',
      civicAddress: {
        addressLine1: '',
        city: '',
        countryRegion: '',
        stateProvince: '',
        postalCode: '',
      },
    };
  }

  public closeCheckoutModal() {
    this.checkoutModal?.close();
    this.onClose();
  }

  public onClose() {
    this.resetCheckout();
    this.isOpen = false;
    this.$nuxt.$emit(UpgradeCheckoutEvents.Close);
  }
}
